import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Field } from '../components/ui/Field'
import { ProviderSyncProgressCard } from '../components/providers/ProviderSyncProgressCard'
import { providerSyncRunActive } from '../components/providers/providerSyncProgress'
import { Pill } from '../components/ui/Pill'
import { Toggle } from '../components/ui/Toggle'
import { Icon, I } from '../components/ui/icons'
import { mobileButtonStyle, MOBILE_SINGLE_COLUMN, TEXT_WRAP_STYLE } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import {
  activateZohoOrderPolicy,
  archiveZohoOrderPolicy,
  createInventoryConnection,
  disableZohoOrderPolicy,
  enableZohoOrderPolicy,
  createZohoOrderPolicy,
  disconnectInventoryConnection,
  getInventoryConnectionSummary,
  getInventoryProductPullRun,
  getZohoOrderRoutingCapabilities,
  importSingleInventoryItem,
  listInventoryConnections,
  listZohoOrderPolicies,
  setZohoOrderPolicyAutoSend,
  startInventoryConnect,
  startInventoryProductPullRun,
} from '../inventory/inventoryApi'
import type {
  InventoryConnection,
  InventoryConnectionStatus,
  InventoryConnectionSummary,
  InventoryProductPullRun,
  InventorySingleItemImportResponse,
  ZohoOrderPolicy,
  ZohoOrderPolicySaveRequest,
} from '../inventory/types'
import { getSourceAuthority, updateSourceAuthority } from '../source/sourceApi'
import type { SourceAuthority } from '../source/types'

interface InventoryZohoDetailPageProps {
  accessToken: string
  onBack?: () => void
  onOpenWizard?: (connectionId: number, policyId?: number | null) => void
  onOpenOrderRouting?: (connectionId: number, policyId?: number | null) => void
  onOpenPullRunDetails?: (connectionId: number, runId: number) => void
  onOpenOrderAudit?: (connectionId: number) => void
  onOpenProduct?: (productId: number, response: InventorySingleItemImportResponse) => void
}

function Stat({ l, v, sub }: { l: string; v: string; sub?: string }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{l}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.01em', marginTop: 4 }}>{v}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

function EmptyState({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <Card padding={22} style={{ borderStyle: 'dashed', background: 'transparent' }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--ink-100)', color: 'var(--fg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={18}>{icon}</Icon>
        </div>
        <div>
          <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--brand-900)' }}>{title}</div>
          <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 4, lineHeight: 1.55, maxWidth: 620 }}>{body}</div>
        </div>
      </div>
    </Card>
  )
}

function policyTone(status: ZohoOrderPolicy['status']): 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'coral' {
  if (status === 'ACTIVE') return 'success'
  if (status === 'DRAFT') return 'warning'
  if (status === 'DISABLED') return 'neutral'
  return 'neutral'
}

function policySummary(policy: ZohoOrderPolicy, fallback: string) {
  return [policy.paymentTermsLabel, policy.deliveryMethodName].filter(Boolean).join(' · ') || fallback
}

function policyInvoiceSourceLabel(policy: ZohoOrderPolicy, t: (key: string) => string) {
  const mode = policy.invoiceSourceMode ?? 'LOCAL_ORDER'
  return t(`pages.syncRuleWizard.invoiceSource.modes.${mode}`)
}

function policyToSavePayload(policy: ZohoOrderPolicy, name: string): ZohoOrderPolicySaveRequest {
  if (policy.routingModelVersion === 2 && policy.routing) {
    return {
      name,
      channelConnectionIds: policy.channelConnectionIds ?? [],
      invoiceSourceMode: policy.invoiceSourceMode ?? 'LOCAL_ORDER',
      actionMappings: (policy.actionMappings ?? []).map((mapping, index) => ({
        priority: mapping.priority ?? index + 1,
        action: mapping.action,
        orderStatuses: Array.from(new Set(mapping.statuses.map(status => status.orderStatus))),
        rawStatuses: Array.from(new Set(mapping.statuses.map(status => status.rawStatus?.trim()).filter(Boolean) as string[])),
      })),
      routingModelVersion: 2,
      routing: { segments: policy.routing.segments.map(segment => ({
        name: segment.name,
        type: segment.type,
        predicates: segment.predicates.map(predicate => ({ field: predicate.field, operator: predicate.operator, lineScope: predicate.lineScope ?? null, values: predicate.values })),
        cells: segment.cells.map(cell => ({ outputKey: cell.outputKey, valueMode: cell.valueMode, externalId: cell.externalId ?? null, secondaryExternalId: cell.secondaryExternalId ?? null, productId: cell.productId ?? null })),
      })) },
    }
  }
  const warehouseOverrides = policy.warehouseOverrides?.length
    ? policy.warehouseOverrides
    : (policy.exceptionRules ?? []).filter(rule => Boolean(rule.warehouseExternalId))

  return {
    name,
    channelConnectionIds: policy.channelConnectionIds ?? [],
    invoiceSourceMode: policy.invoiceSourceMode ?? 'LOCAL_ORDER',
    customerExternalId: policy.customerExternalId ?? null,
    defaultWarehouseExternalId: policy.defaultWarehouseExternalId ?? policy.warehouseExternalId ?? null,
    salesPersonExternalId: policy.salesPersonExternalId ?? null,
    reportingTagExternalId: policy.reportingTagExternalId ?? null,
    reportingTagOptionExternalId: policy.reportingTagOptionExternalId ?? null,
    actionMappings: (policy.actionMappings ?? []).map((mapping, index) => ({
      priority: mapping.priority ?? index + 1,
      action: mapping.action,
      orderStatuses: Array.from(new Set(mapping.statuses.map(status => status.orderStatus))),
      rawStatuses: Array.from(new Set(mapping.statuses.map(status => status.rawStatus?.trim()).filter(Boolean) as string[])),
    })),
    paymentMappings: policy.paymentMappings.map((mapping, index) => ({
      priority: mapping.priority ?? index + 1,
      channelProvider: mapping.channelProvider ?? null,
      channelConnectionId: mapping.channelConnectionId ?? null,
      paymentMethodCode: mapping.paymentMethodCode ?? null,
      paymentMethodName: mapping.paymentMethodName ?? null,
      paymentMethodType: mapping.paymentMethodType ?? null,
      paymentMethodExternalId: mapping.paymentMethodExternalId ?? null,
      paymentStatus: mapping.paymentStatus ?? null,
      shippingMethodExternalId: mapping.shippingMethodExternalId ?? null,
      shippingMethodCode: mapping.shippingMethodCode ?? null,
      shippingCompany: mapping.shippingCompany ?? null,
      shippingMethod: mapping.shippingMethod ?? null,
      paymentTermsExternalId: mapping.paymentTermsExternalId ?? null,
      paymentFeeServiceItemProductId: mapping.paymentFeeServiceItemProductId ?? null,
    })),
    shippingMappings: policy.shippingMappings.map((mapping, index) => ({
      priority: mapping.priority ?? index + 1,
      channelProvider: mapping.channelProvider ?? null,
      channelConnectionId: mapping.channelConnectionId ?? null,
      shippingMethodExternalId: mapping.shippingMethodExternalId ?? null,
      shippingMethodCode: mapping.shippingMethodCode ?? null,
      shippingCompany: mapping.shippingCompany ?? null,
      shippingMethod: mapping.shippingMethod ?? null,
      deliveryMethodExternalId: mapping.deliveryMethodExternalId ?? null,
      shippingFeeServiceItemProductId: mapping.shippingFeeServiceItemProductId ?? null,
    })),
    warehouseOverrides: warehouseOverrides.map((rule, index) => ({
      priority: rule.priority ?? index + 1,
      channelProvider: rule.channelProvider ?? null,
      channelConnectionId: rule.channelConnectionId ?? null,
      orderStatus: rule.orderStatus ?? null,
      rawStatus: rule.rawStatus ?? null,
      paymentStatus: rule.paymentStatus ?? null,
      paymentMethodCode: rule.paymentMethodCode ?? null,
      shippingCompany: rule.shippingCompany ?? null,
      shippingMethod: rule.shippingMethod ?? null,
      fulfillmentChannel: rule.fulfillmentChannel ?? null,
      fulfillmentStatus: rule.fulfillmentStatus ?? null,
      skuMatchScope: rule.skuMatchScope ?? null,
      skuMatchOperator: rule.skuMatchOperator ?? null,
      skuMatchValue: rule.skuMatchValue ?? null,
      warehouseExternalId: rule.warehouseExternalId,
    })),
  }
}

function statusTone(status: InventoryConnectionStatus): 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'coral' {
  if (status === 'CONNECTED') return 'success'
  if (status === 'PENDING') return 'warning'
  if (status === 'AUTH_EXPIRED' || status === 'ERROR') return 'danger'
  return 'neutral'
}

function errorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `pages.inventoryZoho.errors.codes.${error.code}`
  }
  return 'pages.inventoryZoho.errors.codes.REQUEST_FAILED'
}

function singleImportErrorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `pages.inventoryZoho.singleImport.errors.${error.code}`
  }
  return 'pages.inventoryZoho.singleImport.errors.REQUEST_FAILED'
}

type SingleItemLookupKind = 'SKU' | 'ITEM_ID'

type SingleImportBehavior =
  | 'productAndStock'
  | 'productOnly'
  | 'createMissingAndStock'
  | 'stockOnly'
  | 'createMissingOnly'
  | 'blocked'

function authorityOwnedByZoho(authority: SourceAuthority | null, connection: InventoryConnection | null): boolean {
  return Boolean(
    connection
    && authority?.ownerType === 'INVENTORY_CONNECTION'
    && authority.inventoryConnectionId === connection.id,
  )
}

function singleImportBehavior(
  catalogSource: SourceAuthority | null,
  stockSource: SourceAuthority | null,
  connection: InventoryConnection | null,
): SingleImportBehavior {
  const zohoOwnsCatalog = authorityOwnedByZoho(catalogSource, connection)
  const zohoOwnsStock = authorityOwnedByZoho(stockSource, connection)
  if (zohoOwnsCatalog) return zohoOwnsStock ? 'productAndStock' : 'productOnly'
  if (catalogSource?.ownerType === 'TLAAQ') return zohoOwnsStock ? 'createMissingAndStock' : 'createMissingOnly'
  return zohoOwnsStock ? 'stockOnly' : 'blocked'
}

function formatDateTime(value: string | null | undefined, locale: string) {
  if (!value) return null
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
}

export function InventoryZohoDetailPage({ accessToken, onBack, onOpenWizard, onOpenOrderRouting, onOpenPullRunDetails, onOpenOrderAudit, onOpenProduct }: InventoryZohoDetailPageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [connections, setConnections] = useState<InventoryConnection[]>([])
  const [summary, setSummary] = useState<InventoryConnectionSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [routingUiEnabled, setRoutingUiEnabled] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [connecting, setConnecting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [startingRun, setStartingRun] = useState(false)
  const [activeRun, setActiveRun] = useState<InventoryProductPullRun | null>(null)
  const [stockSource, setStockSource] = useState<SourceAuthority | null>(null)
  const [catalogSource, setCatalogSource] = useState<SourceAuthority | null>(null)
  const [catalogSourceLoading, setCatalogSourceLoading] = useState(false)
  const [catalogSourceLoaded, setCatalogSourceLoaded] = useState(false)
  const [catalogSourceLoadFailed, setCatalogSourceLoadFailed] = useState(false)
  const [singleImportOpen, setSingleImportOpen] = useState(false)
  const [singleImportLookupKind, setSingleImportLookupKind] = useState<SingleItemLookupKind>('SKU')
  const [singleImportValue, setSingleImportValue] = useState('')
  const [singleImportSubmitting, setSingleImportSubmitting] = useState(false)
  const [singleImportError, setSingleImportError] = useState<unknown>(null)
  const [lastSingleImportResponse, setLastSingleImportResponse] = useState<InventorySingleItemImportResponse | null>(null)
  const singleImportRequestTokenRef = useRef(0)
  const mountedRef = useRef(true)
  const [policies, setPolicies] = useState<ZohoOrderPolicy[]>([])
  const [policiesLoading, setPoliciesLoading] = useState(false)
  const [activatingPolicyId, setActivatingPolicyId] = useState<number | null>(null)
  const [copyingPolicyId, setCopyingPolicyId] = useState<number | null>(null)
  const [archivingPolicyId, setArchivingPolicyId] = useState<number | null>(null)
  const [disablingPolicyId, setDisablingPolicyId] = useState<number | null>(null)
  const [enablingPolicyId, setEnablingPolicyId] = useState<number | null>(null)
  const [togglingAutoSendPolicyId, setTogglingAutoSendPolicyId] = useState<number | null>(null)
  const [sourceLoading, setSourceLoading] = useState(false)
  const [sourceLoaded, setSourceLoaded] = useState(false)
  const [sourceLoadFailed, setSourceLoadFailed] = useState(false)
  const [settingStockSource, setSettingStockSource] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [authorityReloadKey, setAuthorityReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    setError(null)
    listInventoryConnections(accessToken)
      .then(next => {
        if (!cancelled) setConnections(next)
      })
      .catch(caught => {
        if (!cancelled) setError(caught)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, reloadKey])

  const zohoConnection = useMemo(() => connections.find(connection => connection.provider === 'ZOHO') ?? null, [connections])
  useEffect(() => {
    if (!zohoConnection) return
    let active = true
    getZohoOrderRoutingCapabilities(accessToken, zohoConnection.id).then(capabilities => { if (active) setRoutingUiEnabled(capabilities.uiEnabled) }).catch(() => { if (active) setRoutingUiEnabled(false) })
    return () => { active = false }
  }, [accessToken, zohoConnection])
  const isConnected = zohoConnection?.status === 'CONNECTED'
  const run = activeRun ?? summary?.latestRun ?? null
  const runActive = providerSyncRunActive(run)
  const zohoOwnsStock = Boolean(
    sourceLoaded
    && zohoConnection
    && stockSource?.ownerType === 'INVENTORY_CONNECTION'
    && stockSource.inventoryConnectionId === zohoConnection.id,
  )
  const stockSourcePending = sourceLoading || (!sourceLoaded && !sourceLoadFailed)
  const stockSourceTitleKey = stockSourcePending
    ? 'loading'
    : sourceLoadFailed
      ? 'loadFailed'
      : zohoOwnsStock
        ? 'zohoActive'
        : stockSource?.ownerType === 'TLAAQ'
          ? 'tlaaqActive'
          : 'externalActive'
  const stockSourceBodyKey = stockSourcePending
    ? 'loadingBody'
    : sourceLoadFailed
      ? 'loadFailedBody'
      : zohoOwnsStock
        ? 'zohoActiveBody'
        : stockSource?.ownerType === 'TLAAQ'
          ? 'tlaaqActiveBody'
          : 'externalActiveBody'
  const singleImportAuthoritiesResolved = sourceLoaded && catalogSourceLoaded && !sourceLoadFailed && !catalogSourceLoadFailed
  const singleImportAuthorityFailed = sourceLoadFailed || catalogSourceLoadFailed
  const singleImportAuthorityLoading = sourceLoading || catalogSourceLoading || (!singleImportAuthoritiesResolved && !singleImportAuthorityFailed)
  const singleImportAuthorityBehavior = singleImportBehavior(catalogSource, stockSource, zohoConnection)
  const singleImportSubmitDisabled = (
    singleImportValue.trim().length === 0
    || !singleImportAuthoritiesResolved
    || singleImportAuthorityBehavior === 'blocked'
    || singleImportSubmitting
    || startingRun
    || runActive
  )

  useEffect(() => {
    mountedRef.current = true
    singleImportRequestTokenRef.current += 1
    // A connection change starts a new form/request lifecycle. Any in-flight
    // request may still commit server-side, but its UI completion is stale.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSingleImportOpen(false)
    setSingleImportLookupKind('SKU')
    setSingleImportValue('')
    setSingleImportSubmitting(false)
    setSingleImportError(null)
    setLastSingleImportResponse(null)
    return () => {
      mountedRef.current = false
      singleImportRequestTokenRef.current += 1
    }
  }, [zohoConnection?.id])

  useEffect(() => {
    if (!zohoConnection) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSummary(null)
      setActiveRun(null)
      setStockSource(null)
      setSourceLoaded(false)
      setSourceLoading(false)
      setSourceLoadFailed(false)
      return
    }
    let cancelled = false
    setSummaryLoading(true)
    getInventoryConnectionSummary(accessToken, zohoConnection.id)
      .then(next => {
        if (cancelled) return
        setSummary(next)
        setActiveRun(current => current ?? next.latestRun ?? null)
      })
      .catch(caught => {
        if (!cancelled) setError(caught)
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, zohoConnection, reloadKey])

  useEffect(() => {
    if (!zohoConnection) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStockSource(null)
      setSourceLoaded(false)
      setSourceLoading(false)
      setSourceLoadFailed(false)
      return
    }
    let cancelled = false
    setSourceLoading(true)
    setSourceLoaded(false)
    setSourceLoadFailed(false)
    getSourceAuthority(accessToken, 'STOCK')
      .then(next => {
        if (cancelled) return
        setStockSource(next)
        setSourceLoaded(true)
        setSourceLoadFailed(false)
      })
      .catch(() => {
        if (cancelled) return
        setSourceLoadFailed(true)
      })
      .finally(() => {
        if (!cancelled) setSourceLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, zohoConnection, reloadKey, authorityReloadKey])

  useEffect(() => {
    if (!zohoConnection) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCatalogSource(null)
      setCatalogSourceLoaded(false)
      setCatalogSourceLoading(false)
      setCatalogSourceLoadFailed(false)
      return
    }
    let cancelled = false
    setCatalogSourceLoading(true)
    setCatalogSourceLoaded(false)
    setCatalogSourceLoadFailed(false)
    getSourceAuthority(accessToken, 'PRODUCT_CATALOG')
      .then(next => {
        if (cancelled) return
        setCatalogSource(next)
        setCatalogSourceLoaded(true)
        setCatalogSourceLoadFailed(false)
      })
      .catch(() => {
        if (cancelled) return
        setCatalogSourceLoadFailed(true)
      })
      .finally(() => {
        if (!cancelled) setCatalogSourceLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, zohoConnection, authorityReloadKey])

  useEffect(() => {
    if (!zohoConnection || !run || !providerSyncRunActive(run)) return undefined
    const interval = window.setInterval(() => {
      getInventoryProductPullRun(accessToken, zohoConnection.id, run.id)
        .then(next => {
          setActiveRun(next)
          if (!providerSyncRunActive(next)) {
            getInventoryConnectionSummary(accessToken, zohoConnection.id).then(setSummary).catch(() => {})
          }
        })
        .catch(caught => setError(caught))
    }, run.status === 'RATE_LIMITED' ? 5000 : 1500)
    return () => window.clearInterval(interval)
  }, [accessToken, zohoConnection, run])

  async function handleConnect() {
    setConnecting(true)
    setError(null)
    try {
      const connection = zohoConnection ?? await createInventoryConnection(accessToken, {
        provider: 'ZOHO',
        displayName: 'Zoho Inventory',
      })
      const start = await startInventoryConnect(accessToken, connection.id)
      window.location.assign(start.authorizationUrl)
    } catch (caught) {
      setError(caught)
    } finally {
      setConnecting(false)
    }
  }

  async function handleDisconnect() {
    if (!zohoConnection) return
    setDisconnecting(true)
    setError(null)
    try {
      await disconnectInventoryConnection(accessToken, zohoConnection.id)
      setSummary(null)
      setActiveRun(null)
      setStockSource(null)
      setCatalogSource(null)
      setSourceLoaded(false)
      setSourceLoadFailed(false)
      setCatalogSourceLoaded(false)
      setCatalogSourceLoadFailed(false)
      setReloadKey(current => current + 1)
    } catch (caught) {
      setError(caught)
    } finally {
      setDisconnecting(false)
    }
  }

  async function handleUseZohoStockSource() {
    if (!zohoConnection || !sourceLoaded) return
    if (stockSource?.ownerType !== 'TLAAQ' && !window.confirm(String(t('pages.inventoryZoho.source.confirmReplace')))) return
    setSettingStockSource(true)
    setError(null)
    try {
      const next = await updateSourceAuthority(accessToken, 'STOCK', {
        ownerType: 'INVENTORY_CONNECTION',
        inventoryConnectionId: zohoConnection.id,
        replaceExisting: true,
      })
      setStockSource(next)
    } catch (caught) {
      setError(caught)
    } finally {
      setSettingStockSource(false)
    }
  }

  async function handlePullMissingProducts() {
    if (!zohoConnection || !zohoOwnsStock || startingRun || runActive || singleImportSubmitting) return
    setStartingRun(true)
    setError(null)
    try {
      const response = await startInventoryProductPullRun(accessToken, zohoConnection.id, {
        productMode: 'IMPORT_MISSING',
        stockMode: 'IMPORT_SNAPSHOT',
        pageSize: 100,
      })
      setActiveRun(response.run)
      setSummary(current => current ? { ...current, latestRun: response.run } : current)
    } catch (caught) {
      setError(caught)
    } finally {
      setStartingRun(false)
    }
  }

  function handleSingleImportLookupKindChange(kind: SingleItemLookupKind) {
    if (singleImportSubmitting || kind === singleImportLookupKind) return
    setSingleImportLookupKind(kind)
    setSingleImportValue('')
    setSingleImportError(null)
    setLastSingleImportResponse(null)
  }

  function handleCloseSingleImport() {
    if (singleImportSubmitting) return
    singleImportRequestTokenRef.current += 1
    setSingleImportOpen(false)
    setSingleImportLookupKind('SKU')
    setSingleImportValue('')
    setSingleImportSubmitting(false)
    setSingleImportError(null)
    setLastSingleImportResponse(null)
  }

  function handleRetrySingleImportAuthorities() {
    setSingleImportError(null)
    setAuthorityReloadKey(current => current + 1)
  }

  async function handleSingleImport(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!zohoConnection || singleImportSubmitDisabled) return
    const value = singleImportValue.trim()
    const requestToken = singleImportRequestTokenRef.current + 1
    singleImportRequestTokenRef.current = requestToken
    setSingleImportSubmitting(true)
    setSingleImportError(null)
    setLastSingleImportResponse(null)
    try {
      const response = await importSingleInventoryItem(
        accessToken,
        zohoConnection.id,
        singleImportLookupKind === 'SKU' ? { sku: value } : { externalItemId: value },
      )
      if (!mountedRef.current || singleImportRequestTokenRef.current !== requestToken) return
      setSingleImportValue('')
      setLastSingleImportResponse(response)
      onOpenProduct?.(response.product.id, response)
    } catch (caught) {
      if (!mountedRef.current || singleImportRequestTokenRef.current !== requestToken) return
      setSingleImportError(caught)
    } finally {
      if (mountedRef.current && singleImportRequestTokenRef.current === requestToken) {
        setSingleImportSubmitting(false)
      }
    }
  }

  function openZoho() {
    window.open('https://inventory.zoho.com/app', '_blank', 'noopener,noreferrer')
  }

  useEffect(() => {
    if (!zohoConnection) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPolicies([])
      setPoliciesLoading(false)
      return
    }
    let cancelled = false
    setPoliciesLoading(true)
    listZohoOrderPolicies(accessToken, zohoConnection.id)
      .then(next => {
        if (!cancelled) setPolicies(next)
      })
      .catch(caught => {
        if (!cancelled) setError(caught)
      })
      .finally(() => {
        if (!cancelled) setPoliciesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, zohoConnection, reloadKey])

  async function handleActivatePolicy(policy: ZohoOrderPolicy) {
    if (!zohoConnection) return
    setActivatingPolicyId(policy.id)
    setError(null)
    try {
      const activated = await activateZohoOrderPolicy(accessToken, zohoConnection.id, policy.id)
      setPolicies(current => [activated, ...current
        .filter(existing => existing.id !== activated.id)
        .map(existing => existing.status === 'ACTIVE' ? { ...existing, status: 'DISABLED' as const } : existing)])
    } catch (caught) {
      setError(caught)
    } finally {
      setActivatingPolicyId(null)
    }
  }

  async function handleCopyPolicyToDraft(policy: ZohoOrderPolicy) {
    if (!zohoConnection) return
    setCopyingPolicyId(policy.id)
    setError(null)
    try {
      const draft = await createZohoOrderPolicy(
        accessToken,
        zohoConnection.id,
        policyToSavePayload(policy, String(t('pages.inventoryZoho.rules.copyName', { name: policy.name }))),
      )
      setPolicies(current => [draft, ...current])
      if (draft.routingModelVersion === 2) onOpenOrderRouting?.(zohoConnection.id, draft.id)
      else onOpenWizard?.(zohoConnection.id, draft.id)
    } catch (caught) {
      setError(caught)
    } finally {
      setCopyingPolicyId(null)
    }
  }

  async function handleEnablePolicy(policy: ZohoOrderPolicy) {
    if (!zohoConnection) return
    setEnablingPolicyId(policy.id)
    setError(null)
    try {
      const enabled = await enableZohoOrderPolicy(accessToken, zohoConnection.id, policy.id)
      setPolicies(current => [enabled, ...current
        .filter(existing => existing.id !== enabled.id)
        .map(existing => existing.status === 'ACTIVE' ? { ...existing, status: 'DISABLED' as const } : existing)])
    } catch (caught) {
      setError(caught)
    } finally {
      setEnablingPolicyId(null)
    }
  }

  async function handleToggleAutoSend(policy: ZohoOrderPolicy, enabled: boolean) {
    if (!zohoConnection) return
    setTogglingAutoSendPolicyId(policy.id)
    setError(null)
    try {
      const updated = await setZohoOrderPolicyAutoSend(accessToken, zohoConnection.id, policy.id, { enabled })
      setPolicies(current => current.map(existing => existing.id === updated.id ? updated : existing))
    } catch (caught) {
      setError(caught)
    } finally {
      setTogglingAutoSendPolicyId(null)
    }
  }

  async function handleDisablePolicy(policy: ZohoOrderPolicy) {
    if (!zohoConnection) return
    if (!window.confirm(String(t('pages.inventoryZoho.rules.confirmDisableActive', { name: policy.name })))) return
    setDisablingPolicyId(policy.id)
    setError(null)
    try {
      const disabled = await disableZohoOrderPolicy(accessToken, zohoConnection.id, policy.id)
      setPolicies(current => current.map(existing => existing.id === disabled.id ? disabled : existing))
    } catch (caught) {
      setError(caught)
    } finally {
      setDisablingPolicyId(null)
    }
  }

  async function handleArchivePolicy(policy: ZohoOrderPolicy) {
    if (!zohoConnection) return
    const confirmKey = policy.status === 'DISABLED' ? 'confirmArchiveDisabled' : 'confirmArchiveDraft'
    if (!window.confirm(String(t(`pages.inventoryZoho.rules.${confirmKey}`, { name: policy.name })))) return
    setArchivingPolicyId(policy.id)
    setError(null)
    try {
      await archiveZohoOrderPolicy(accessToken, zohoConnection.id, policy.id)
      setPolicies(current => current.map(existing => existing.id === policy.id ? { ...existing, status: 'ARCHIVED' as const } : existing))
    } catch (caught) {
      setError(caught)
    } finally {
      setArchivingPolicyId(null)
    }
  }

  const activePolicyCount = policies.filter(policy => policy.status === 'ACTIVE').length
  const draftPolicyCount = policies.filter(policy => policy.status === 'DRAFT').length
  const disabledPolicyCount = policies.filter(policy => policy.status === 'DISABLED').length
  const visiblePolicies = policies.filter(policy => policy.status !== 'ARCHIVED')
  const latestActivity = formatDateTime(summary?.latestRun?.finishedAt ?? summary?.latestRun?.updatedAt, i18n.language)
  const runTerminal = run?.status === 'FAILED' || run?.status === 'SUCCEEDED' || run?.status === 'SUCCEEDED_WITH_ISSUES'
  const showRunDetailsCta = Boolean(
    zohoConnection
    && run
    && runTerminal
    && (run.status === 'FAILED' || run.status === 'SUCCEEDED_WITH_ISSUES' || run.issuesCount > 0 || run.productsSkipped > 0 || run.stockRowsSkipped > 0),
  )

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 13, color: 'var(--fg-subtle)', marginBottom: 18 }}>
        <button onClick={onBack} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--fg-muted)', fontFamily: 'inherit', fontSize: 13 }}>{t('nav.items.inventory')}</button>
        <span>›</span>
        <span style={{ color: 'var(--fg)', fontWeight: 600 }}>{t('pages.inventoryZoho.title')}</span>
      </div>

      <Card padding={isMobile ? 18 : 24} style={{ marginBottom: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'auto minmax(0, 1fr) auto', gap: isMobile ? 16 : 16, alignItems: 'center' }}>
          <div style={{ width: 52, height: 52, borderRadius: 12, background: 'linear-gradient(135deg, #C8202F, #E94B47)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 24, fontFamily: 'Nunito, sans-serif', flexShrink: 0 }}>Z</div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: 0, lineHeight: 1.2, margin: 0 }}>{t('pages.inventoryZoho.title')}</h2>
              <Pill tone={zohoConnection ? statusTone(zohoConnection.status) : 'neutral'} dot>
                {loading
                  ? t('pages.inventoryZoho.connection.loading')
                  : zohoConnection
                    ? t(`pages.inventoryZoho.connection.status.${zohoConnection.status}`)
                    : t('pages.inventoryZoho.connection.notConnected')}
              </Pill>
            </div>
            <div style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 4 }}>
              {isConnected && zohoConnection?.externalAccountId
                ? (
                  <>
                    {t('pages.inventoryZoho.connection.organization')} <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--fg)', fontWeight: 600 }}>{zohoConnection.externalAccountId}</span>
                  </>
                )
                : String(t('pages.inventoryZoho.connection.connectHint'))}
            </div>
          </div>
          <div style={{ display: isMobile ? 'grid' : 'flex', gap: 8, flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end', width: isMobile ? '100%' : undefined }}>
            {isConnected && (
              <Button
                kind="secondary"
                size="sm"
                icon={I.plus}
                onClick={() => singleImportOpen ? handleCloseSingleImport() : setSingleImportOpen(true)}
                disabled={singleImportSubmitting}
                style={mobileButtonStyle(isMobile)}
              >
                {singleImportOpen ? t('pages.inventoryZoho.singleImport.closeAction') : t('pages.inventoryZoho.singleImport.action')}
              </Button>
            )}
            {isConnected && (
              <Button kind="primary" size="sm" onClick={handlePullMissingProducts} disabled={startingRun || runActive || singleImportSubmitting || sourceLoading || !sourceLoaded || !zohoOwnsStock} style={mobileButtonStyle(isMobile)}>
                {startingRun ? t('pages.inventoryZoho.actions.startingPull') : runActive ? t('pages.inventoryZoho.actions.pullActive') : t('pages.inventoryZoho.actions.pullMissingProducts')}
              </Button>
            )}
            {isConnected && zohoConnection && <Button kind="secondary" size="sm" icon={I.shield} onClick={() => onOpenOrderAudit?.(zohoConnection.id)} style={mobileButtonStyle(isMobile)}>{t('pages.inventoryZoho.actions.orderAudit')}</Button>}
            {isConnected && <Button kind="ghost" size="sm" onClick={openZoho} style={mobileButtonStyle(isMobile)}>{t('pages.inventoryZoho.actions.openZoho')}</Button>}
            {zohoConnection && (
              <Button kind={isConnected ? 'ghost' : 'primary'} size="sm" onClick={handleConnect} disabled={connecting || loading} style={mobileButtonStyle(isMobile)}>
                {connecting
                  ? t('pages.inventoryZoho.actions.connecting')
                  : isConnected
                    ? t('pages.inventoryZoho.actions.reauthorize')
                    : t('pages.inventoryZoho.actions.reconnect')}
              </Button>
            )}
            {isConnected ? (
              <Button kind="secondary" size="sm" onClick={handleDisconnect} disabled={disconnecting} style={mobileButtonStyle(isMobile)}>
                {disconnecting ? t('pages.inventoryZoho.actions.disconnecting') : t('pages.inventoryZoho.actions.disconnect')}
              </Button>
            ) : !zohoConnection && (
              <Button kind="primary" size="sm" onClick={handleConnect} disabled={connecting || loading} style={mobileButtonStyle(isMobile)}>
                {connecting ? t('pages.inventoryZoho.actions.connecting') : t('pages.inventoryZoho.actions.connect')}
              </Button>
            )}
          </div>
        </div>

        {isConnected && (
          <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 14, border: `1px solid ${zohoOwnsStock ? 'var(--success-100)' : 'var(--border-default)'}`, background: zohoOwnsStock ? 'var(--success-100)' : 'var(--ink-50)', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 14, alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between' }}>
            <div style={TEXT_WRAP_STYLE}>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: zohoOwnsStock ? 'var(--success)' : 'var(--brand-900)' }}>
                {t(`pages.inventoryZoho.source.${stockSourceTitleKey}`)}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 4, lineHeight: 1.5 }}>
                {t(`pages.inventoryZoho.source.${stockSourceBodyKey}`)}
              </div>
            </div>
            {sourceLoaded && !zohoOwnsStock && (
              <Button kind="secondary" size="sm" onClick={handleUseZohoStockSource} disabled={sourceLoading || settingStockSource} style={mobileButtonStyle(isMobile)}>
                {settingStockSource ? t('pages.inventoryZoho.actions.settingStockSource') : t('pages.inventoryZoho.actions.setStockSource')}
              </Button>
            )}
          </div>
        )}

        {error !== null && (
          <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 10, background: 'var(--danger-100)', color: 'var(--danger)', fontSize: 13, lineHeight: 1.45 }}>
            {String(t(errorMessageKey(error), { defaultValue: String(t('pages.inventoryZoho.errors.codes.REQUEST_FAILED')) }))}
          </div>
        )}

        {isConnected && singleImportOpen && (
          <Card padding={isMobile ? 16 : 20} style={{ marginTop: 16, background: 'var(--ink-50)', borderColor: 'var(--border-default)' }}>
            <form onSubmit={handleSingleImport}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'minmax(0, 1fr) auto', gap: 12, alignItems: 'start' }}>
                <div style={{ minWidth: 0 }}>
                  <h3 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 16, fontWeight: 800 }}>
                    {t('pages.inventoryZoho.singleImport.title')}
                  </h3>
                  <p style={{ margin: '4px 0 0', color: 'var(--fg-muted)', fontSize: 12.5, lineHeight: 1.5, ...TEXT_WRAP_STYLE }}>
                    {t('pages.inventoryZoho.singleImport.subtitle')}
                  </p>
                </div>
                <Pill tone="info">{t('pages.inventoryZoho.singleImport.bounded')}</Pill>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'repeat(2, minmax(0, 1fr))', gap: 8, marginTop: 16 }} data-testid="single-import-lookup-selector">
                <Button
                  kind={singleImportLookupKind === 'SKU' ? 'primary' : 'secondary'}
                  size="sm"
                  ariaPressed={singleImportLookupKind === 'SKU'}
                  onClick={() => handleSingleImportLookupKindChange('SKU')}
                  disabled={singleImportSubmitting}
                  style={mobileButtonStyle(isMobile)}
                >
                  {t('pages.inventoryZoho.singleImport.lookup.sku')}
                </Button>
                <Button
                  kind={singleImportLookupKind === 'ITEM_ID' ? 'primary' : 'secondary'}
                  size="sm"
                  ariaPressed={singleImportLookupKind === 'ITEM_ID'}
                  onClick={() => handleSingleImportLookupKindChange('ITEM_ID')}
                  disabled={singleImportSubmitting}
                  style={mobileButtonStyle(isMobile)}
                >
                  {t('pages.inventoryZoho.singleImport.lookup.itemId')}
                </Button>
              </div>

              <Field
                label={t(singleImportLookupKind === 'SKU' ? 'pages.inventoryZoho.singleImport.field.skuLabel' : 'pages.inventoryZoho.singleImport.field.itemIdLabel')}
                placeholder={t(singleImportLookupKind === 'SKU' ? 'pages.inventoryZoho.singleImport.field.skuPlaceholder' : 'pages.inventoryZoho.singleImport.field.itemIdPlaceholder')}
                hint={t(singleImportLookupKind === 'SKU' ? 'pages.inventoryZoho.singleImport.field.skuHelp' : 'pages.inventoryZoho.singleImport.field.itemIdHelp')}
                value={singleImportValue}
                onChange={event => setSingleImportValue(event.target.value)}
                disabled={singleImportSubmitting}
                maxLength={singleImportLookupKind === 'SKU' ? 128 : 255}
                dir="ltr"
                inputStyle={{ fontFamily: 'var(--font-mono)', fontSize: 12, minWidth: 0 }}
                style={{ marginTop: 14, minWidth: 0 }}
              />

              <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 12, border: `1px solid ${singleImportAuthorityBehavior === 'blocked' && singleImportAuthoritiesResolved ? 'var(--danger-100)' : 'var(--border-subtle)'}`, background: 'white' }}>
                {singleImportAuthorityLoading ? (
                  <div style={{ color: 'var(--fg-muted)', fontSize: 12.5, lineHeight: 1.5 }}>
                    <strong style={{ color: 'var(--brand-900)' }}>{t('pages.inventoryZoho.singleImport.authority.loading')}</strong>
                    <div style={{ marginTop: 3 }}>{t('pages.inventoryZoho.singleImport.authority.loadingBody')}</div>
                  </div>
                ) : singleImportAuthorityFailed ? (
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'minmax(0, 1fr) auto', gap: 10, alignItems: 'center' }}>
                    <div style={{ minWidth: 0, color: 'var(--danger)', fontSize: 12.5, lineHeight: 1.5, ...TEXT_WRAP_STYLE }}>
                      <strong>{t('pages.inventoryZoho.singleImport.authority.loadFailed')}</strong>
                      <div style={{ marginTop: 3 }}>{t('pages.inventoryZoho.singleImport.authority.loadFailedBody')}</div>
                    </div>
                    <Button kind="secondary" size="sm" onClick={handleRetrySingleImportAuthorities} style={mobileButtonStyle(isMobile)}>
                      {t('pages.inventoryZoho.singleImport.authority.retry')}
                    </Button>
                  </div>
                ) : (
                  <div style={{ color: singleImportAuthorityBehavior === 'blocked' ? 'var(--danger)' : 'var(--fg)', fontSize: 12.5, lineHeight: 1.5, ...TEXT_WRAP_STYLE }}>
                    <strong>{t(`pages.inventoryZoho.singleImport.authority.behavior.${singleImportAuthorityBehavior}.title`)}</strong>
                    <div style={{ marginTop: 3 }}>{t(`pages.inventoryZoho.singleImport.authority.behavior.${singleImportAuthorityBehavior}.body`)}</div>
                    <div style={{ marginTop: 7, color: 'var(--fg-subtle)' }}>{t('pages.inventoryZoho.singleImport.authority.serverRevalidates')}</div>
                  </div>
                )}
              </div>

              {singleImportError !== null && (
                <div role="alert" style={{ marginTop: 12, padding: '10px 12px', borderRadius: 10, background: 'var(--danger-100)', color: 'var(--danger)', fontSize: 13, lineHeight: 1.45, ...TEXT_WRAP_STYLE }}>
                  {String(t(singleImportErrorMessageKey(singleImportError), { defaultValue: String(t('pages.inventoryZoho.singleImport.errors.REQUEST_FAILED')) }))}
                </div>
              )}

              {lastSingleImportResponse !== null && !onOpenProduct && (
                <div role="status" style={{ marginTop: 12, padding: '11px 13px', borderRadius: 10, border: '1px solid var(--success-100)', background: 'white', color: 'var(--success)', fontSize: 12.5, lineHeight: 1.5, ...TEXT_WRAP_STYLE }}>
                  <strong>{t(`pages.inventoryZoho.singleImport.outcomes.${lastSingleImportResponse.outcome}`)}</strong>
                  <div style={{ marginTop: 3, color: 'var(--fg)' }}>
                    {t('pages.inventoryZoho.singleImport.resultSummary', {
                      stock: lastSingleImportResponse.result.stockRowsImported,
                      warehouses: lastSingleImportResponse.result.warehousesCreated,
                      issues: lastSingleImportResponse.result.issues.length,
                    })}
                  </div>
                </div>
              )}

              <div style={{ display: isMobile ? 'grid' : 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                <Button kind="ghost" size="sm" onClick={handleCloseSingleImport} disabled={singleImportSubmitting} style={mobileButtonStyle(isMobile)}>
                  {t('pages.inventoryZoho.singleImport.cancel')}
                </Button>
                <Button kind="primary" size="sm" type="submit" disabled={singleImportSubmitDisabled} style={mobileButtonStyle(isMobile)}>
                  {singleImportSubmitting ? t('pages.inventoryZoho.singleImport.submitting') : t('pages.inventoryZoho.singleImport.submit')}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {run && (
          <ProviderSyncProgressCard
            run={run}
            title={t('pages.inventoryZoho.pull.progressTitle')}
            finalSummary={t('pages.inventoryZoho.pull.finalCounters', {
              fetched: run.fetched,
              created: run.productsCreated,
              updated: run.productsUpdated,
              skipped: run.productsSkipped,
              issues: run.issuesCount,
            })}
            details={showRunDetailsCta && zohoConnection ? (
              <Button kind="secondary" size="sm" icon={I.eye} onClick={() => onOpenPullRunDetails?.(zohoConnection.id, run.id)} style={mobileButtonStyle(isMobile)}>
                {t('pages.inventoryZoho.pull.viewDetails')}
              </Button>
            ) : undefined}
          />
        )}

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4,1fr)', gap: isMobile ? 14 : 22, marginTop: 22, padding: '16px 0 0', borderTop: '1px solid var(--border-subtle)' }}>
          <Stat l={t('pages.inventoryZoho.stats.products')} v={summaryLoading && !summary ? t('pages.inventoryZoho.stats.loading') : summary ? String(summary.productCount) : t('pages.inventoryZoho.stats.notAvailable')} sub={summary ? t('pages.inventoryZoho.stats.productsImported') : t('pages.inventoryZoho.stats.productsPending')} />
          <Stat l={t('pages.inventoryZoho.stats.warehouses')} v={summaryLoading && !summary ? t('pages.inventoryZoho.stats.loading') : summary ? String(summary.mappedWarehouseCount) : t('pages.inventoryZoho.stats.notAvailable')} sub={summary ? t('pages.inventoryZoho.stats.warehousesMapped') : t('pages.inventoryZoho.stats.warehousesPending')} />
          <Stat l={t('pages.inventoryZoho.stats.syncRules')} v={policiesLoading ? t('pages.inventoryZoho.stats.loading') : String(visiblePolicies.length)} sub={policiesLoading ? t('pages.inventoryZoho.stats.syncRulesLoading') : t('pages.inventoryZoho.stats.syncRulesConfigured', { active: activePolicyCount, draft: draftPolicyCount, disabled: disabledPolicyCount })} />
          <Stat l={t('pages.inventoryZoho.stats.lastActivity')} v={latestActivity ?? t('pages.inventoryZoho.stats.notAvailable')} sub={summary?.latestRun ? t(`pages.inventoryZoho.pull.status.${summary.latestRun.status}`) : t('pages.inventoryZoho.stats.lastActivityPending')} />
        </div>
      </Card>

      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-end', gap: 12, marginBottom: 12 }}>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.01em', margin: 0 }}>{t('pages.inventoryZoho.rules.title')}</h3>
          <p style={{ fontSize: 12.5, color: 'var(--fg-muted)', margin: '4px 0 0', maxWidth: 540 }}>
            {t('pages.inventoryZoho.rules.subtitle')}
          </p>
        </div>
        <Button kind="secondary" size="sm" icon={I.plus} disabled={!isConnected || !zohoConnection} onClick={() => zohoConnection && (routingUiEnabled ? onOpenOrderRouting : onOpenWizard)?.(zohoConnection.id, null)} style={mobileButtonStyle(isMobile)}>{t('pages.inventoryZoho.actions.newRule')}</Button>
      </div>
      {policiesLoading ? (
        <Card padding={22} style={{ borderStyle: 'dashed', background: 'transparent' }}>
          <div style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{t('pages.inventoryZoho.rules.loading')}</div>
        </Card>
      ) : visiblePolicies.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 12 }}>
          {visiblePolicies.map(policy => {
            const autoSendEnabled = policy.autoSendEnabled === true
            const autoSendBusy = togglingAutoSendPolicyId === policy.id
            const policyBusy = activatingPolicyId === policy.id || copyingPolicyId === policy.id || archivingPolicyId === policy.id || disablingPolicyId === policy.id || enablingPolicyId === policy.id || autoSendBusy
            return (
              <Card key={policy.id} padding={18} style={{ background: policy.status === 'ACTIVE' ? 'var(--success-100)' : 'white' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0 }}>
                    <Pill tone={policyTone(policy.status)} dot>{t(`pages.syncRuleWizard.status.${policy.status}`)}</Pill>
                    <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--brand-900)', marginTop: 10, ...TEXT_WRAP_STYLE }}>{policy.name}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 4 }}>{t('pages.inventoryZoho.rules.version', { version: policy.version })}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 6, lineHeight: 1.45, ...TEXT_WRAP_STYLE }}>{policySummary(policy, t('pages.inventoryZoho.rules.noDefaults'))}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 4, lineHeight: 1.45, ...TEXT_WRAP_STYLE }}>
                      {t('pages.inventoryZoho.rules.invoiceSource', { source: policyInvoiceSourceLabel(policy, t) })}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 12, border: '1px solid var(--border-subtle)', background: 'white', display: 'grid', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', minWidth: 0 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--brand-900)' }}>{t('pages.inventoryZoho.rules.autoSend.title')}</div>
                      <div style={{ fontSize: 12.5, color: autoSendEnabled ? 'var(--success)' : 'var(--fg-muted)', marginTop: 2, fontWeight: 700 }}>
                        {autoSendEnabled ? t('pages.inventoryZoho.rules.autoSend.enabled') : t('pages.inventoryZoho.rules.autoSend.disabled')}
                      </div>
                    </div>
                    <Toggle
                      on={autoSendEnabled}
                      onChange={enabled => handleToggleAutoSend(policy, enabled)}
                      disabled={policy.status !== 'ACTIVE' || autoSendBusy}
                      ariaLabel={t('pages.inventoryZoho.rules.autoSend.toggleLabel', { name: policy.name })}
                    />
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
                    {t(policy.status === 'ACTIVE' ? 'pages.inventoryZoho.rules.autoSend.help' : 'pages.inventoryZoho.rules.autoSend.activeOnlyHelp')}
                  </div>
                  {autoSendBusy && <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>{t('pages.inventoryZoho.rules.autoSend.saving')}</div>}
                </div>
                {zohoConnection && (
                  <div style={{ display: isMobile ? 'grid' : 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                    <Button kind="secondary" size="sm" onClick={() => policy.routingModelVersion === 2 ? onOpenOrderRouting?.(zohoConnection.id, policy.id) : onOpenWizard?.(zohoConnection.id, policy.id)} disabled={policyBusy} style={mobileButtonStyle(isMobile)}>
                      {policy.status === 'DRAFT' ? t('pages.inventoryZoho.actions.editDraft') : t('pages.inventoryZoho.actions.viewPolicy')}
                    </Button>
                    {routingUiEnabled && policy.routingModelVersion !== 2 && onOpenOrderRouting && <Button kind="secondary" size="sm" onClick={() => onOpenOrderRouting(zohoConnection.id, policy.id)} disabled={policyBusy} style={mobileButtonStyle(isMobile)}>{t('pages.orderRouting.title')}</Button>}
                    {policy.status === 'ACTIVE' && (
                      <Button kind="secondary" size="sm" onClick={() => handleCopyPolicyToDraft(policy)} disabled={policyBusy} style={mobileButtonStyle(isMobile)}>
                        {copyingPolicyId === policy.id ? t('pages.inventoryZoho.actions.copyingPolicy') : t('pages.inventoryZoho.actions.copyToDraft')}
                      </Button>
                    )}
                    {policy.status === 'DRAFT' && (
                      <Button kind="coral" size="sm" onClick={() => handleActivatePolicy(policy)} disabled={policyBusy} style={mobileButtonStyle(isMobile)}>
                        {activatingPolicyId === policy.id ? t('pages.inventoryZoho.actions.activating') : t('pages.inventoryZoho.actions.activateDraft')}
                      </Button>
                    )}
                    {policy.status === 'DISABLED' && (
                      <Button kind="coral" size="sm" onClick={() => handleEnablePolicy(policy)} disabled={policyBusy} style={mobileButtonStyle(isMobile)}>
                        {enablingPolicyId === policy.id ? t('pages.inventoryZoho.actions.enablingPolicy') : t('pages.inventoryZoho.actions.enablePolicy')}
                      </Button>
                    )}
                    {policy.status === 'ACTIVE' ? (
                      <Button kind="secondary" size="sm" onClick={() => handleDisablePolicy(policy)} disabled={policyBusy} style={mobileButtonStyle(isMobile)}>
                        {disablingPolicyId === policy.id ? t('pages.inventoryZoho.actions.disablingPolicy') : t('pages.inventoryZoho.actions.disablePolicy')}
                      </Button>
                    ) : (
                      <Button kind="danger" size="sm" onClick={() => handleArchivePolicy(policy)} disabled={policyBusy} style={mobileButtonStyle(isMobile)}>
                        {archivingPolicyId === policy.id ? t('pages.inventoryZoho.actions.archivingPolicy') : t('pages.inventoryZoho.actions.archivePolicy')}
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      ) : (
        <EmptyState
          icon={I.clock}
          title={t('pages.inventoryZoho.rules.emptyTitle')}
          body={isConnected ? t('pages.inventoryZoho.rules.emptyBody') : t('pages.inventoryZoho.rules.connectFirstBody')}
        />
      )}

      <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.01em', margin: '28px 0 12px' }}>{t('pages.inventoryZoho.activity.title')}</h3>
      <EmptyState
        icon={I.box}
        title={t('pages.inventoryZoho.activity.emptyTitle')}
        body={t('pages.inventoryZoho.activity.emptyBody')}
      />
    </div>
  )
}
